import Icon from '@ant-design/icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import { useMemo } from 'react';

import { getFileIcon } from '@/components/chat-app/components/fileCard';
import { HighLight } from '@/components/highLight';
import type { INoteAttachment } from '@/service/note';
import { EMemoryType } from '@/service/note';
import {
  getDefaultDescription,
  isFileAttachment,
  isImageAttachment,
  isVoiceFileAttachment
} from '@/ui/components/memory-detail/components/attachments/utils';
import { formatDuration } from '@/utils/timestamp-format';
import { getMainDomain } from '@/utils/utils';

import ScrollCard from '../scrollCard';
import styles from './index.less';

interface IProps {
  className?: string;
  attachments: INoteAttachment[];
  mouseEnter?: boolean;
  memoryType?: EMemoryType;
  highlightTitle?: string;
}

dayjs.extend(durationPlugin);

const renderCreateTimeString = (str?: string) => {
  return !str ? undefined : dayjs(str).format('YYYY-MM-DD');
};

const NoteShowAttachment = (props: IProps) => {
  const { className, attachments, mouseEnter, memoryType, highlightTitle } = props;
  const attachment = attachments[0];
  const isAllImageAttachment = useMemo(() => {
    return memoryType === EMemoryType.IMAGE && attachments.every((item) => isImageAttachment(item));
  }, [attachments, memoryType]);

  const { icon, image, title, description, url } = attachment.metadata?.ogi || {};
  const _title = useMemo(() => {
    return highlightTitle || title;
  }, [highlightTitle, title]);

  if (!attachment.metadata?.ogi) {
    return null;
  }

  if (isAllImageAttachment) {
    return attachments.length >= 3 ? (
      <ScrollCard className={styles.showAttachment} list={attachments} mouseEnter={mouseEnter} />
    ) : (
      <div className={styles.imageAttachments}>
        {attachments.map((item) => (
          <img
            key={item.id}
            className={classNames(styles.imageAttachment, className)}
            src={item.url}
          />
        ))}
      </div>
    );
  }

  if (isImageAttachment(attachment) && !isAllImageAttachment) {
    return (
      <img
        key={attachment.id}
        className={classNames(styles.imageAttachment, className)}
        src={attachment.url}
      />
    );
  }

  if (isFileAttachment(attachment)) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const { svg, color } = getFileIcon(attachment.metadata?.ogi.type || '');

    return (
      <div className={classNames(styles.fileAttachment, className)}>
        <Icon className="text-[32px] w-8 h-8" component={svg} style={{ color: color }} />
        <div className="flex flex-col flex-1 gap-[2px] overflow-hidden">
          <HighLight className="text-sm font-medium leading-6 text-primary-black line-clamp-1 w-full">
            {_title}
          </HighLight>
          {isVoiceFileAttachment(attachment) ? (
            <div className="flex items-center text-xs leading-5 text-[--primary-dark-gray]">
              <div>{renderCreateTimeString(attachment.metadata.audio?.createTimestamp)}</div>
              <div className="ml-auto">
                {formatDuration((attachment.metadata.audio?.duration || 0) * 1000)}
              </div>
            </div>
          ) : (
            <div className="text-xs leading-5 text-[--primary-dark-gray] line-clamp-1">
              {description || getDefaultDescription(attachment)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.linkAttachment, className)}>
      {image == icon ? null : (
        <img
          className="w-[62px] h-[62px] object-cover rounded-[4px]"
          referrerPolicy="no-referrer"
          src={image}
        />
      )}
      <div className="flex flex-col flex-1 gap-[2px] overflow-hidden">
        <div className="flex items-center w-full">
          <img className="w-3 h-3 !m-0 !mr-2" src={icon} />
          <div className="text-xs leading-5 text-primary-black flex-1 line-clamp-1">
            {getMainDomain(url)}
          </div>
        </div>
        {!!title && (
          <HighLight className="text-sm font-medium leading-6 text-primary-black line-clamp-1">
            {_title}
          </HighLight>
        )}
        {!!description && (
          <div className="text-xs leading-5 text-[--primary-dark-gray] line-clamp-1">
            {description}
          </div>
        )}
      </div>
    </div>
  );
};

export default NoteShowAttachment;
